import { Box, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { toneTextColour } from "../../../polish/LabelValueRow";
import { cardSx, TABULAR, type TonalTokens } from "../../../polish/tokens";
import { cardHeadingSx } from "../feed/headings";
import { EMPTY, targetColour } from "../feed/NoteParts";
import {
  noteSummary,
  signedPercent,
  signTone,
  type NoteRow,
  type TargetMoves,
} from "../feed/feedModel";
import { Pager, usePaged } from "./Pager";

const ROW_HEIGHT = 48;
export const TARGETS_PAGE_SIZE = 8;

interface ChangePillProps {
  row: NoteRow;
  tokens: TonalTokens;
}

/** "+8.3%" on a tint of its colour; a raise or cut without the old target shows its arrow alone. */
const ChangePill = ({ row, tokens }: ChangePillProps) => {
  let text = EMPTY;
  let tone: "gain" | "loss" | null = null;
  if (row.targetTone === "gain" || row.targetTone === "loss")
    tone = row.targetTone;
  if (row.change !== null) {
    text = signedPercent(row.change);
    const sign = signTone(row.change);
    tone = sign === "flat" ? null : sign;
  }
  if (row.change === null && tone === "gain") text = "↑";
  if (row.change === null && tone === "loss") text = "↓";
  const colour = tone === null ? tokens.muted : toneTextColour(tone, tokens);
  const fill =
    tone === null
      ? alpha(tokens.text, 0.06)
      : alpha(tone === "gain" ? tokens.gain : tokens.loss, 0.14);
  return (
    <Box
      component="span"
      data-testid="target-change"
      sx={{
        ...TABULAR,
        justifySelf: "end",
        minWidth: 64,
        boxSizing: "border-box",
        px: 1,
        py: 0.25,
        borderRadius: "6px",
        textAlign: "center",
        fontSize: { xs: "0.8125rem", md: "0.875rem" },
        lineHeight: "20px",
        fontWeight: 600,
        color: colour,
        backgroundColor: fill,
      }}
    >
      {text}
    </Box>
  );
};

interface TargetTextProps {
  row: NoteRow;
  tokens: TonalTokens;
}

/** "$360 → $390": the old target quiet, the new one larger in its move's colour. */
const TargetText = ({ row, tokens }: TargetTextProps) => (
  <Typography
    component="span"
    data-testid="target-text"
    noWrap
    sx={{
      ...TABULAR,
      display: "flex",
      alignItems: "baseline",
      gap: 0.75,
      fontSize: { xs: "0.875rem", md: "0.9375rem" },
      lineHeight: "22px",
      color: tokens.muted,
    }}
  >
    {row.targetBefore !== null && (
      <>
        <span>{row.targetBefore}</span>
        <Box component="span" aria-hidden sx={{ color: tokens.faint }}>
          →
        </Box>
      </>
    )}
    <Box
      component="span"
      data-testid="target-after"
      sx={{
        fontSize: { xs: "0.9375rem", md: "1rem" },
        fontWeight: 600,
        color: targetColour(row, tokens),
      }}
    >
      {row.targetAfter}
    </Box>
  </Typography>
);

interface PriceTargetsCardProps {
  moves: TargetMoves;
  tokens: TonalTokens;
  /** Firm on its own line, the target and its change under it. */
  touch: boolean;
}

/**
 * Each firm's latest target, highest first, eight a page: the firm, the old
 * target → the new one in its move's colour (green raised, red cut, grey
 * kept), and the change on a tint of the same colour.
 */
export const PriceTargetsCard = ({
  moves,
  tokens,
  touch,
}: PriceTargetsCardProps) => {
  const paged = usePaged(moves.moves, TARGETS_PAGE_SIZE);
  return (
    <Box
      component="section"
      aria-labelledby="price-targets-title"
      data-testid="price-targets-card"
      sx={{ ...cardSx(tokens), px: { xs: 2, md: 3 }, py: { xs: 2, md: 2.5 } }}
    >
      <Typography
        id="price-targets-title"
        component="h2"
        sx={{ ...cardHeadingSx(tokens), mb: 1.5 }}
      >
        Price targets
      </Typography>
      <Box
        component="ul"
        ref={paged.listRef}
        sx={{ listStyle: "none", m: 0, p: 0, minHeight: paged.listMinHeight }}
      >
        {paged.shown.map((move) => (
          <Box
            component="li"
            key={move.id}
            data-testid="price-target-row"
            aria-label={noteSummary(move.row)}
            sx={{
              display: "grid",
              alignItems: "center",
              columnGap: 2,
              borderTop: `1px solid ${tokens.hairline}`,
              ...(touch && {
                gridTemplateColumns: "minmax(0, 1fr) auto",
                rowGap: 0.5,
                py: 1.25,
              }),
              ...(!touch && {
                gridTemplateColumns: "minmax(0, 1fr) auto 72px",
                minHeight: ROW_HEIGHT,
              }),
            }}
          >
            <Typography
              noWrap
              data-testid="target-firm"
              sx={{
                fontSize: { xs: "0.875rem", md: "0.9375rem" },
                fontWeight: 500,
                color: tokens.text,
                minWidth: 0,
                ...(touch && { gridColumn: "1 / -1" }),
              }}
            >
              {move.firm}
            </Typography>
            <TargetText row={move.row} tokens={tokens} />
            <ChangePill row={move.row} tokens={tokens} />
          </Box>
        ))}
      </Box>
      <Pager
        page={paged.page}
        pages={paged.pages}
        pageSize={TARGETS_PAGE_SIZE}
        total={moves.moves.length}
        onPage={paged.setPage}
        tokens={tokens}
        label="Price targets"
      />
    </Box>
  );
};
